import { useState } from "react";
import { SeoHead } from "@/components/SeoHead";
import { PageTransition } from "@/components/layout/PageTransition";
import { motion, AnimatePresence } from "framer-motion";
import { X, ZoomIn, Images } from "lucide-react";
import { useCms } from "@/hooks/useCms";
import { useQuery } from "@tanstack/react-query";
import { db } from "@/lib/firebase";
import { collection, getDocs, query, orderBy } from "firebase/firestore";

type DbImage = { id: string; image_url: string; title: string; caption: string; category: string; display_order: number };

const DEFAULT_GALLERY_IMAGES = [
  { id: "1", src: "https://images.unsplash.com/photo-1551958219-acbc25b32f8e?w=800&auto=format&fit=crop", alt: "Football training session", category: "Academy" },
  { id: "2", src: "https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=800&auto=format&fit=crop", alt: "Players on the pitch", category: "Academy" },
  { id: "3", src: "https://images.unsplash.com/photo-1553778263-73a83bab9b0c?w=800&auto=format&fit=crop", alt: "Scouting tournament", category: "Scouting" },
  { id: "4", src: "https://images.unsplash.com/photo-1517466787929-bc90951d0974?w=800&auto=format&fit=crop", alt: "Coach and athlete", category: "Academy" },
  { id: "5", src: "https://images.unsplash.com/photo-1543351611-58f69d7c1781?w=800&auto=format&fit=crop", alt: "Youth talent showcase", category: "Events" },
  { id: "6", src: "https://images.unsplash.com/photo-1508098682722-e99c43a406b2?w=800&auto=format&fit=crop", alt: "Player signing ceremony", category: "Transfers" },
  { id: "7", src: "https://images.unsplash.com/photo-1579952363873-27f3bade9f55?w=800&auto=format&fit=crop", alt: "Football skills training", category: "Academy" },
  { id: "8", src: "https://images.unsplash.com/photo-1606925797300-0b35e9d1794e?w=800&auto=format&fit=crop", alt: "Scout evaluating talent", category: "Scouting" },
  { id: "9", src: "https://images.unsplash.com/photo-1518604666860-9ed391f76460?w=800&auto=format&fit=crop", alt: "Community sports event", category: "Events" },
  { id: "10", src: "https://images.unsplash.com/photo-1522778526097-ce0a22cdbec6?w=800&auto=format&fit=crop", alt: "Football stadium atmosphere", category: "Events" },
  { id: "11", src: "https://images.unsplash.com/photo-1560272564-c83b66b1ad12?w=800&auto=format&fit=crop", alt: "International transfer deal", category: "Transfers" },
  { id: "12", src: "https://images.unsplash.com/photo-1547347298-4074fc3086f0?w=800&auto=format&fit=crop", alt: "Young football player", category: "Scouting" },
  { id: "13", src: "https://images.unsplash.com/photo-1489944440615-453fc2b6a9a9?w=800&auto=format&fit=crop", alt: "Team celebration", category: "Events" },
  { id: "14", src: "https://images.unsplash.com/photo-1612872087720-bb876e2e67d1?w=800&auto=format&fit=crop", alt: "Goalkeeper training", category: "Academy" },
  { id: "15", src: "https://images.unsplash.com/photo-1536122985607-4fe00f965a3e?w=800&auto=format&fit=crop", alt: "Youth league match", category: "Events" },
  { id: "16", src: "https://images.unsplash.com/photo-1516802273409-68526ee1bdd6?w=800&auto=format&fit=crop", alt: "Basketball training", category: "Academy" },
];

export default function Gallery() {
  const { getMeta, getText } = useCms();

  const heroSubtitle = getText("gallery_hero", "Moments from our academies, scouting events, transfers, and community programs across the globe.");

  const { data: dbImages = [] } = useQuery<DbImage[]>({
    queryKey: ["/api/gallery"],
    queryFn: async () => {
      try {
        const q = query(collection(db, "gallery_images"), orderBy("display_order", "asc"));
        const snap = await getDocs(q);
        return snap.docs.map(d => {
          const item = d.data();
          return {
            id: d.id,
            image_url: item.image_url || "",
            title: item.title || "",
            caption: item.caption || "",
            category: item.category || "General",
            display_order: item.display_order || 0,
          };
        });
      } catch (err) {
        console.error("Failed to fetch gallery from Firestore:", err);
        return [];
      }
    },
    staleTime: 1000 * 60 * 5,
  });

  const galleryMeta = getMeta<{ items?: Array<{ src: string; alt: string; category: string }> }>("gallery_items", { items: [] });

  const rawItems = dbImages.length > 0
    ? dbImages.map(img => ({ id: img.id, src: img.image_url, alt: img.caption || img.title || "", category: img.category || "General" }))
    : galleryMeta.items && galleryMeta.items.length > 0
      ? galleryMeta.items.map((item, i) => ({ ...item, id: i + 1 }))
      : DEFAULT_GALLERY_IMAGES;

  const categories = ["All", ...Array.from(new Set(rawItems.map(img => img.category)))];
  const [activeCategory, setActiveCategory] = useState("All");
  const [lightboxImg, setLightboxImg] = useState<{ src: string; alt: string } | null>(null);

  const filtered = activeCategory === "All" ? rawItems : rawItems.filter(img => img.category === activeCategory);

  return (
    <>
      <SeoHead
        title="Gallery"
        description="Browse the Eliada Sports Agency gallery — images from our training programs, athlete development sessions, and international events that showcase the world of sports management."
        path="/gallery"
      />
      <PageTransition className="pt-24 pb-24">
      <div className="relative py-28 bg-secondary overflow-hidden border-b border-white/5">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1543351611-58f69d7c1781?w=2000&auto=format&fit=crop" alt="" aria-hidden="true" className="w-full h-full object-cover object-center opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-background/60 via-background/30 to-background/90" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary/40 bg-primary/10 text-primary mb-6">
            <Images size={13} />
            <span className="text-xs font-display tracking-widest uppercase font-semibold">Media Gallery</span>
          </motion.div>
          <h1 className="text-5xl md:text-7xl font-display font-bold text-white mb-5">OUR <span className="text-primary">GALLERY</span></h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">{heroSubtitle}</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-16">
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map(cat => (
            <button key={cat} onClick={() => setActiveCategory(cat)} className={`px-6 py-2.5 rounded-full font-display uppercase tracking-widest text-sm font-semibold transition-all duration-300 ${activeCategory === cat ? "bg-primary text-primary-foreground shadow-[0_0_20px_rgba(255,191,0,0.3)]" : "bg-secondary text-gray-400 border border-white/10 hover:border-primary/40 hover:text-white"}`}>
              {cat}
            </button>
          ))}
        </div>

        <motion.div layout className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-4 space-y-4">
          <AnimatePresence>
            {filtered.map((img) => (
              <motion.div key={img.id} layout initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }} transition={{ duration: 0.3 }} className="relative group break-inside-avoid mb-4 overflow-hidden rounded-xl cursor-pointer border border-white/5 hover:border-primary/50 transition-colors bg-secondary" onClick={() => setLightboxImg({ src: img.src, alt: img.alt })}>
                <img src={img.src} alt={img.alt} className="w-full object-cover transition-transform duration-500 group-hover:scale-110" loading="lazy" style={{ display: "block", minHeight: "120px" }} />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-all duration-300 flex items-center justify-center">
                  <ZoomIn className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 w-10 h-10" />
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/80 to-transparent translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                  <p className="text-xs text-gray-300 font-medium">{img.alt}</p>
                  <span className="text-xs text-primary font-display uppercase tracking-wider">{img.category}</span>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>

        {filtered.length === 0 && (
          <div className="text-center py-24 text-muted-foreground">No images in this category yet.</div>
        )}
      </div>

      <AnimatePresence>
        {lightboxImg && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-md flex items-center justify-center p-4" onClick={() => setLightboxImg(null)}>
            <button className="absolute top-6 right-6 w-12 h-12 rounded-full bg-white/10 hover:bg-primary flex items-center justify-center text-white transition-colors z-10" onClick={() => setLightboxImg(null)}><X size={22} /></button>
            <motion.img initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.8, opacity: 0 }} src={lightboxImg.src.replace("w=800", "w=1400")} alt={lightboxImg.alt} className="max-w-full max-h-[85vh] object-contain rounded-xl shadow-2xl" onClick={e => e.stopPropagation()} />
          </motion.div>
        )}
      </AnimatePresence>
      </PageTransition>
    </>
  );
}
